import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { attributesApi } from '../lib/api';
import { Tag, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function Attributes() {
  const [search, setSearch]         = useState('');
  const [keyFilter, setKeyFilter]   = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['attributes'],
    queryFn: attributesApi.get,
  });

  const attributeKeys = data?.attributeKeys ?? [];

  const filtered = useMemo(() => {
    const users = data?.users ?? [];
    return users.filter(u => {
      const matchesSearch =
        !search ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        `${u.givenName} ${u.familyName}`.toLowerCase().includes(search.toLowerCase());

      const matchesKey =
        !keyFilter ||
        Object.keys(u.customAttributes).includes(keyFilter);

      return matchesSearch && matchesKey;
    });
  }, [data, search, keyFilter]);

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex-1">
        <h1 className="text-2xl font-semibold text-foreground">Atributos</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Atributos personalizados de Cognito por usuario.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar usuario..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={cn(inputCls, 'pl-9')}
          />
        </div>
        <select
          value={keyFilter}
          onChange={e => setKeyFilter(e.target.value)}
          className={cn(inputCls, 'sm:w-60')}
        >
          <option value="">Todos los atributos</option>
          {attributeKeys.map(k => (
            <option key={k} value={k}>{k}</option>
          ))}
        </select>
      </div>

      {/* Summary pills */}
      {attributeKeys.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attributeKeys.map(k => (
            <button
              key={k}
              onClick={() => setKeyFilter(prev => prev === k ? '' : k)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                keyFilter === k
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:border-primary hover:text-primary'
              )}
            >
              <Tag className="h-3 w-3" />
              {k.replace('custom:', '')}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando...</p>
      ) : attributeKeys.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center shadow-sm">
          <Tag className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground text-sm">
            No se encontraron atributos personalizados en ningún usuario.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Los atributos deben tener el prefijo <code className="bg-muted px-1 rounded">custom:</code> en Cognito.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center shadow-sm">
          <p className="text-muted-foreground text-sm">Sin resultados para la búsqueda actual.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase whitespace-nowrap">
                    Usuario
                  </th>
                  {(keyFilter ? [keyFilter] : attributeKeys).map(k => (
                    <th
                      key={k}
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase whitespace-nowrap font-mono"
                    >
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(u => {
                  const cols = keyFilter ? [keyFilter] : attributeKeys;
                  return (
                    <tr key={u.username} className="hover:bg-muted/40 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-foreground">
                          {u.givenName} {u.familyName}
                        </div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </td>
                      {cols.map(k => {
                        const val = u.customAttributes[k];
                        return (
                          <td key={k} className="px-4 py-3 whitespace-nowrap">
                            {val !== undefined ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                                <Tag className="h-3 w-3" />
                                {val}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/30 text-xs">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {filtered.length} usuario{filtered.length !== 1 ? 's' : ''} · {attributeKeys.length} atributo{attributeKeys.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = 'block w-full border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
